import { getEnumKeyByValue } from "@helpers/enumHelper";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Ethnicity, Race, Sex } from "@store/generalTypes";
import { format } from "date-fns";
import { isEmpty } from "lodash";

export enum NewExamineeStepNames {
    ContactInfo = "ContactInfo",
    Details = "Details",
    AssignedTo = "AssignedTo"
}

export interface ExamineeContactInfo {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber: string;
}

export interface ExamineeDetails {
    sex: Sex;
    birthDay: string;
    birthMonth: string;
    birthYear: string;
    race: Race;
    ethnicity: Ethnicity;
    highestLevelEducation: string;
    primaryLanguage: string;
    secondaryLanguages: string[];
}

export interface NewExamineeId {
    id?: string;
    displayId?: string;
}

export interface Examinee {
    ids?: NewExamineeId;
    contactInfo: ExamineeContactInfo;
    examineeDetails?: ExamineeDetails;
    assignedTo?: ExamineeAssignedTo;
    lastAssessment?: string;
}

export interface ExamineeAssignedTo {
    location: number;
    clinicianId: string;
}

const emptyContactInfo: ExamineeContactInfo = {
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: ""
};

export const initialState: Examinee = {
    contactInfo: emptyContactInfo
};

const newExamineeSlice = createSlice({
    name: "newExaminee",
    initialState,
    reducers: {
        updateContactInfo(state, action: PayloadAction<ExamineeContactInfo>) {
            state.contactInfo.email = action.payload.email;
            state.contactInfo.phoneNumber = action.payload.phoneNumber;
            state.contactInfo.firstName = action.payload.firstName;
            state.contactInfo.lastName = action.payload.lastName;
            state.contactInfo.id = action.payload.id;
        },
        updateExamineeDetails(state, action: PayloadAction<ExamineeDetails>) {
            state.examineeDetails = action.payload;
        },
        updateExamineeAssignedTo(state, action: PayloadAction<ExamineeAssignedTo>) {
            state.assignedTo = action.payload;
        },
        resetExaminee(state) {
            state.contactInfo = emptyContactInfo;
            state.examineeDetails = undefined;
            state.assignedTo = undefined;
            state.ids = undefined;
        },
        setExamineeId(state, action: PayloadAction<NewExamineeId>) {
            state.ids = action.payload;
        }
    }
});

export interface NormalizedExaminee {
    clinicianId: string;
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber: string;
    birthDay: string;
    birthMonth: string;
    birthYear: string;
    sex: string;
    race: string;
    ethnicity: string;
    highestLevelEducation: string;
    primaryLanguage: string;
    secondaryLanguages?: string[];
}

export interface NewExamineeApi {
    clinicianId: string;
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber: string;
    birthDate: string;
    sex: string;
    race: string;
    ethnicity: string;
    highestLevelEducation: string;
    primaryLanguage: string;
    secondaryLanguages?: string[];
}

const getBirthDate = (birthYear: number, birthMonth: number, birthDay: number): string => {
    return format(new Date(birthYear, birthMonth - 1, birthDay), "yyyy-MM-dd");
};

export const parseNewExamineeForApi = (examinee: Examinee): NewExamineeApi => {
    const { contactInfo, examineeDetails, assignedTo } = examinee;

    const normalizedExaminee: NormalizedExaminee = {
        // Assigned To
        clinicianId: assignedTo!.clinicianId,
        // Contact Info
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        phoneNumber: contactInfo.phoneNumber,
        email: contactInfo.email,
        // Details
        ethnicity: examineeDetails!.ethnicity,
        race: examineeDetails!.race,
        sex: examineeDetails!.sex,
        highestLevelEducation: examineeDetails!.highestLevelEducation,
        primaryLanguage: examineeDetails!.primaryLanguage,
        secondaryLanguages: examineeDetails!.secondaryLanguages,
        birthDay: examineeDetails!.birthDay,
        birthMonth: examineeDetails!.birthMonth,
        birthYear: examineeDetails!.birthYear
    };

    const newExaminee = parseExamineeForApi(normalizedExaminee);

    return newExaminee;
};

export const parseExamineeForApi = (examinee: NormalizedExaminee): NewExamineeApi => {
    const birthDate = getBirthDate(Number(examinee.birthYear), Number(examinee.birthMonth), Number(examinee.birthDay));

    const newExaminee: NewExamineeApi = {
        // Assigned To
        clinicianId: examinee.clinicianId,
        // Contact Info
        firstName: examinee.firstName,
        lastName: examinee.lastName,
        phoneNumber: examinee.phoneNumber,
        // Details
        ethnicity: getEnumKeyByValue(Ethnicity, examinee.ethnicity) ?? "",
        race: getEnumKeyByValue(Race, examinee.race) ?? "",
        sex: examinee.sex,
        highestLevelEducation: examinee.highestLevelEducation,
        primaryLanguage: examinee.primaryLanguage,
        secondaryLanguages: examinee.secondaryLanguages?.reduce(
            (filteredLangs, currentLang) =>
                !currentLang || filteredLangs.includes(currentLang) ? filteredLangs : [...filteredLangs, currentLang],
            Array<string>()
        ),
        birthDate
    };

    if (examinee.email) {
        newExaminee.email = examinee.email;
    }

    const secondaryLanguages = examinee.secondaryLanguages?.filter((x) => !isEmpty(x));

    if (secondaryLanguages?.length) {
        newExaminee.secondaryLanguages = secondaryLanguages;
    }

    return newExaminee;
};

export const { updateContactInfo, resetExaminee, updateExamineeDetails, updateExamineeAssignedTo, setExamineeId } =
    newExamineeSlice.actions;

export default newExamineeSlice.reducer;
